import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

/**
 * ---------- Assets ----------
 * A: white "half-panel" curtain — bottom half nearly opaque white, top half transparent,
 *    with a natural wavy edge in between. Used TWICE:
 *      1) original orientation → sits at bottom, wave edge points up ("lower curtain")
 *      2) scaleY(-1) mirrored  → sits at top,    wave edge points down ("upper curtain")
 * B: flower bouquet.
 * C: noise-textured gradient background (pink / lilac / periwinkle).
 */
const IMG_CURTAIN =
  "https://static.readdy.ai/image/ef1aae41220ad17a1705abffff22a58b/e4415a7e1071c5a489707ef8182b9b1f.png";
const IMG_FLOWER =
  "https://storage.readdy-site.link/project_files/6f693711-6af4-4a33-b314-a141ce391a59/eb3b461b-5f68-483e-8e57-2a87b6072eab_compressed_unnamed.webp";
const IMG_TEXTURE =
  "https://static.readdy.ai/image/ef1aae41220ad17a1705abffff22a58b/69d7c90f8a934f837b54305782671c16.png";
const IMG_ACCENT_BLOOM =
  "https://static.readdy.ai/image/ef1aae41220ad17a1705abffff22a58b/06b4a4af4002ab705beae531e91cb89e.png";

const promises = [
  {
    tag: "Promise 01",
    title: "Curated Blooms, Delivered Fresh",
    desc: "Hand-picked seasonal stems from our partner farms, sealed within hours to keep every petal at peak.",
  },
  {
    tag: "Promise 02",
    title: "Bespoke Design, One of a Kind",
    desc: "Our floral atelier composes each arrangement around your story — colour, silhouette and mood tailored .",
  },
  {
    tag: "Promise 03",
    title: "Aftercare Without Worry",
    desc: "From vase-life guidance to complimentary refresh service, the beauty of your gift is looked&nbsp;",
  },
];

export default function HeroScroll() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const textureRef = useRef<HTMLDivElement>(null);
  const curtainTopRef = useRef<HTMLDivElement>(null);
  const curtainBottomRef = useRef<HTMLDivElement>(null);
  const flowerRef = useRef<HTMLDivElement>(null);
  const flowerRightRef = useRef<HTMLDivElement>(null);
  const accentBloomRef = useRef<HTMLDivElement>(null);
  const heroTitleRef = useRef<HTMLDivElement>(null);
  const heroTitleBottomRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<HTMLDivElement>(null);
  const promisesRef = useRef<HTMLDivElement>(null);
  const scrollHintRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // ---------- Initial states ----------
      // Two curtains meet in the middle — wavy edges align perfectly on the vertical centerline.
      gsap.set(curtainTopRef.current, { yPercent: 0 });
      gsap.set(curtainBottomRef.current, { yPercent: 0 });
      // Both flowers (left & right) are visible from the start (no opacity fade).
      // They begin WAY below the stage (fully hidden behind the lower curtain) and slowly
      // rise up into view during Phase 2. Right flower starts slightly later for a stagger.
      gsap.set(flowerRef.current, { opacity: 1, yPercent: 90 });
      gsap.set(flowerRightRef.current, { opacity: 1, yPercent: 95 });
      // Right-center accent bloom starts fully visible in place.
      // TransformOrigin biased toward the upper-right so the "collapse to a dot"
      // exit converges on a point near the top-right corner of the bloom.
      gsap.set(accentBloomRef.current, {
        opacity: 1,
        yPercent: 0,
        scale: 1,
        rotate: 0,
        transformOrigin: "85% 30%",
      });
      // Texture is technically always there at the bottom of the stack, no animation needed.
      gsap.set(textureRef.current, { opacity: 1 });
      // Logo & promises hidden.
      gsap.set(logoRef.current, { opacity: 0, y: 20 });
      gsap.set(promisesRef.current?.children ?? [], { opacity: 0, y: 20 });

      // ---------- Master timeline (300vh scrub-pinned) ----------
      const tl = gsap.timeline({
        defaults: { ease: "none" },
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top top",
          end: "+=300%",
          scrub: true,
          pin: stageRef.current,
          pinSpacing: true,
          anticipatePin: 1,
        },
      });

      // ===== Phase 1 (0 → 0.42): staged, narrative exit of the intro layer =====
      // Everything in the intro layer leaves in a choreographed order so the
      // scroll feels like a scene changing on a stage, not a single cut:
      //   ① The left-side headline is the first thing to acknowledge the scroll:
      //      it lifts and fades quickly (the reader's own text drifts out of the way).
      //   ② A beat later, the right-side accent bloom drops down like a petal
      //      being carried away by gravity — a slower, weightier motion.
      //   ③ Slightly after that, the upper mirrored curtain pulls UP and the
      //      lower curtain sinks DOWN — the theatre curtains part, revealing
      //      the noise-textured stage behind. They are intentionally offset by
      //      a small delta so it doesn't feel mechanically symmetrical.
      // Different eases reinforce the story: text uses a sharp power3.in (a quick
      // exit), the bloom uses power2.out (a soft, floaty drop), and the curtains
      // use power3.inOut (a deliberate, ceremonial reveal).

      // ① Hero title — first to leave (starts at 0, fastest)
      // Top block lifts up and out, bottom block sinks down and out — a split reveal.
      tl.to(
        heroTitleRef.current,
        { opacity: 0, y: -40, duration: 0.18, ease: "power3.in" },
        0
      );
      tl.to(
        heroTitleBottomRef.current,
        { opacity: 0, y: 40, duration: 0.18, ease: "power3.in" },
        0
      );

      // ③ Curtains — the main ceremonial reveal, mid-tempo
      // Upper curtain leads by a beat so the eye tracks it first.
      tl.to(
        curtainTopRef.current,
        { yPercent: -55, duration: 0.34, ease: "power3.inOut" },
        0.08
      );
      tl.to(
        curtainBottomRef.current,
        { yPercent: 55, duration: 0.34, ease: "power3.inOut" },
        0.12
      );

      // ② Right accent bloom — slides straight down and fades out.
      // Simple, weighty gravity drop — no scaling, no rotation. Just a clean
      // downward exit like a petal falling out of frame.
      tl.to(
        accentBloomRef.current,
        {
          yPercent: 110,
          opacity: 0,
          duration: 0.32,
          ease: "power2.in",
        },
        0.12
      );

      // ===== Phase 2 (0.46 → 0.90): the bouquets rise up like a slow bloom =====
      // A distinct beat of stillness (0.42 → 0.46) lets the scene reset before
      // the flowers appear, so the reader feels the pause before the reveal.
      // Left rises first with a soft ease-out, right follows 0.08 later with a
      // slower back-ease that overshoots almost imperceptibly — gives the
      // right bouquet a heavier, "settling into place" personality.
      tl.to(
        flowerRef.current,
        { yPercent: 0, duration: 0.5, ease: "power2.out" },
        0.46
      );
      tl.to(
        flowerRightRef.current,
        { yPercent: 0, duration: 0.58, ease: "back.out(1.05)" },
        0.54
      );

      // ===== Phase 3a (0.85 → 0.92): brand logo fades + rises =====
      tl.to(
        logoRef.current,
        { opacity: 1, y: 0, duration: 0.07, ease: "power2.out" },
        0.85
      );

      // ===== Phase 3b (0.92 → 1.00): promise columns stagger in =====
      if (promisesRef.current) {
        tl.to(
          promisesRef.current.children,
          {
            opacity: 1,
            y: 0,
            duration: 0.06,
            ease: "power2.out",
            stagger: 0.06,
          },
          0.92
        );
      }

      // ---------- Scroll hint fades out immediately when scrolling begins ----------
      gsap.to(scrollHintRef.current, {
        opacity: 0,
        y: 10,
        ease: "none",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top top",
          end: "+=10%",
          scrub: true,
        },
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative bg-white"
      aria-label="Blossom Haven hero"
    >
      <div
        ref={stageRef}
        className="relative h-screen w-full overflow-hidden bg-white"
      >
        {/* ==================== 4-layer stack ====================
            z-index order (strictly enforced):
              lower curtain (z-40)  >  flower (z-30)  >  upper mirrored curtain (z-20)  >  texture (z-10)
            The flower rises through the middle gap and passes in front of the
            upper curtain but behind the lower curtain — giving a real depth illusion.
        ======================================================= */}

        {/* Layer 1 (z-10) — noise-gradient texture, fills the full stage.
             Initially fully hidden by the two curtains meeting in the middle.
             As the curtains slide apart, this layer is revealed in the gap. */}
        <div ref={textureRef} className="absolute inset-0 z-10">
          <img
            src={IMG_TEXTURE}
            alt=""
            aria-hidden="true"
            className="h-full w-full object-cover"
          />
        </div>

        {/* Layer 2 (z-20) — UPPER curtain (image A mirrored with scaleY(-1)).
             Covers the top half of the stage, wavy edge facing DOWN. */}
        <div
          ref={curtainTopRef}
          className="absolute inset-0 z-20 will-change-transform"
          style={{ transform: "scaleY(-1)" }}
        >
          <img
            src={IMG_CURTAIN}
            alt=""
            aria-hidden="true"
            className="h-full w-full object-cover"
          />
        </div>

        {/* Layer 3a (z-30) — Flower bouquet (LEFT), always visible.
             Starts hidden behind the lower curtain (yPercent 90) and slowly rises to
             its final position during Phase 2. */}
        <div
          ref={flowerRef}
          className="pointer-events-none absolute inset-0 z-30 flex items-end justify-start will-change-transform"
        >
          <img
            src={IMG_FLOWER}
            alt="Blossom Haven signature bouquet"
            className="h-[72vh] w-auto max-w-none -translate-x-[10%] -translate-y-[22%] select-none object-contain"
            draggable={false}
          />
        </div>

        {/* Layer 3b (z-30) — Flower bouquet (RIGHT), mirrored copy for right-side balance.
             Same rising animation as the left flower but starts slightly later for a
             natural staggered "blooming" feel. Horizontally flipped so the two sides
             don't look like an exact duplicate. */}
        <div
          ref={flowerRightRef}
          className="pointer-events-none absolute inset-0 z-30 flex items-end justify-end will-change-transform"
        >
          <img
            src={IMG_FLOWER}
            alt=""
            aria-hidden="true"
            className="h-[112vh] w-auto max-w-none select-none object-contain"
            style={{ transform: "scaleX(-1) translateX(-22%) translateY(6%)" }}
            draggable={false}
          />
        </div>

        {/* Layer 3c (z-30) — right-center accent bloom.
             Sits BETWEEN the two white curtains (above upper curtain z-20, below lower
             curtain z-40) so it truly looks "sandwiched" between them.
             On scroll it slides downward and fades out with the lower curtain. */}
        <div
          ref={accentBloomRef}
          className="pointer-events-none absolute inset-y-0 right-0 z-30 flex items-center justify-end will-change-transform"
        >
          <img
            src={IMG_ACCENT_BLOOM}
            alt=""
            aria-hidden="true"
            className="h-[86vh] w-auto max-w-none translate-x-[6%] select-none object-contain md:h-[94vh]"
            draggable={false}
          />
        </div>

        {/* Layer 4 (z-40, HIGHEST) — LOWER curtain (image A, original orientation).
             Covers the bottom half of the stage, wavy edge facing UP.
             Because its z-index is above the flower, it clips the flower's rise path,
             creating the "flower emerging from behind the curtain" effect. */}
        <div
          ref={curtainBottomRef}
          className="absolute inset-0 z-40 will-change-transform"
        >
          <img
            src={IMG_CURTAIN}
            alt=""
            aria-hidden="true"
            className="h-full w-full object-cover"
          />
        </div>

        {/* ==================== UI overlays (z-50) ==================== */}

        {/* ==================== Hero title — SPLIT into two blocks ====================
             The editorial display title is intentionally broken into two vertically
             separated pieces so the composition uses the full height of the hero:
               • TOP block  → tagline ("Spring Atelier · 2026") + first line "EVERY BLOOM"
                             sits in the upper white area, left-aligned.
               • BOTTOM block → italic second line "tells a story", plus the descriptive
                             paragraph and CTAs. Rests DOWN on the lower white curtain,
                             right-aligned to form a diagonal editorial rhythm with the
                             top block (upper-left ↔ lower-right).
             Both blocks share the same z-50 layer so they remain above the curtains,
             and both animate out on scroll (top lifts up, bottom sinks down). */}

        {/* TOP title block — tagline + "EVERY BLOOM" */}
        <div
          ref={heroTitleRef}
          className="pointer-events-none absolute left-10 right-10 top-[30%] z-50 md:left-16 md:right-16 lg:left-24 lg:right-24"
        >
          <div className="mb-6 flex items-center gap-3 text-[10px] font-light uppercase tracking-[0.42em] text-neutral-500">
            <span className="h-px w-10 bg-neutral-400" />
            Spring Atelier · 2026
          </div>
          <h1 className="font-swash-display font-bold italic uppercase leading-[0.86] tracking-[-0.01em] text-haven-ink">
            <span className="block text-[12vw] md:text-[10vw] lg:text-[9.5vw]">
              Bloom
            </span>
          </h1>
          <div className="mt-8 pointer-events-auto flex items-center gap-5">
            <a
              href="#collections"
              className="cursor-pointer whitespace-nowrap rounded-full bg-haven-ink px-6 py-3 text-[11px] uppercase tracking-[0.28em] text-white transition-colors hover:bg-neutral-800"
            >
              Discover Collections
            </a>
            <a
              href="#story"
              className="group cursor-pointer whitespace-nowrap text-[11px] uppercase tracking-[0.28em] text-haven-ink"
            >
              Our Story
              <span className="ml-2 inline-block transition-transform group-hover:translate-x-1">
                →
              </span>
            </a>
          </div>
        </div>

        {/* BOTTOM title block — italic "tells a story" + description + CTAs.
             Positioned down on the lower white curtain, right-aligned so it forms
             a diagonal with the upper-left "EVERY BLOOM". */}
        <div
          ref={heroTitleBottomRef}
          className="pointer-events-none absolute bottom-[9%] left-10 right-10 z-50 md:left-16 md:right-16 lg:left-24 lg:right-24"
        >
          <h2 className="font-swash-display text-left font-light italic uppercase leading-[0.86] tracking-[-0.01em] text-neutral-700">
            <span className="block text-[8vw] md:text-[7vw] lg:text-[6.5vw]">
              tells a story
            </span>
          </h2>
          <div className="mt-6">
            <p className="max-w-[420px] text-[13px] font-light leading-relaxed text-neutral-600 md:text-[14px]">
              Hand-composed arrangements from our seasonal atelier — designed to
              speak the language of moments, gifted with grace.
            </p>
          </div>
        </div>

        {/* Top-left brand logo — enlarged, refined Playfair Display serif with more presence */}
        <div
          ref={logoRef}
          className="absolute left-10 top-[92px] z-50 flex items-start gap-4 md:left-16 md:top-[100px]"
        >
          <span className="font-serif-display text-6xl font-normal italic leading-[0.85] text-haven-ink md:text-7xl">
            X
          </span>
          <div className="pt-2">
            <div className="font-serif-display text-3xl font-medium leading-none tracking-tight text-haven-ink md:text-4xl">
              Blossom Haven
            </div>
            <div className="mt-2 text-[10px] font-light tracking-[0.32em] text-neutral-500 md:text-[11px]">
              SPEAKING IN BLOOMS · GRACING EVERY MOMENT
            </div>
          </div>
        </div>

        {/* Top-center nav */}
        <nav className="absolute left-1/2 top-10 z-50 hidden -translate-x-1/2 items-center gap-8 text-sm tracking-wide text-neutral-600 md:flex">
          <a
            href="#collections"
            className="cursor-pointer transition-colors hover:text-haven-ink"
          >
            Collections
          </a>
          <a
            href="#story"
            className="cursor-pointer transition-colors hover:text-haven-ink"
          >
            Story
          </a>
          <a
            href="#atelier"
            className="cursor-pointer transition-colors hover:text-haven-ink"
          >
            Atelier
          </a>
          <a
            href="#contact"
            className="cursor-pointer whitespace-nowrap rounded-full border border-haven-ink/80 bg-white/60 px-4 py-2 text-xs uppercase tracking-[0.2em] text-haven-ink backdrop-blur-sm transition-colors hover:bg-haven-ink hover:text-white"
          >
            Order Now
          </a>
        </nav>

        {/* Three "Promise" blocks — no card background, placed in the empty corners so
             the flowers stay unobstructed. Layout:
               • Promise 01 & 02 stacked in the bottom-left white curtain area
               • Promise 03 sits in the top-right area under the nav
             The wrapper is a zero-size container that only exists to give the animation
             timeline a stable children list to stagger. */}
        <div ref={promisesRef} className="pointer-events-none">
          {/* Diagonal composition across the whole hero:
                Promise 01 — middle-left, in the left curtain white area
                Promise 02 — bottom-left, deeper into the corner (shifted right & down)
                Promise 03 — top-right, under the nav
             This creates a diagonal reading path from bottom-left to top-right,
             while keeping the flowers unobstructed. */}

          {/* Promise 02 — dead center of the hero */}
          <div
            className="pointer-events-auto absolute left-1/2 z-50 max-w-[300px] -translate-x-1/2 -translate-y-1/2 text-right md:max-w-[340px]"
            style={{ top: "calc(50% - 160px)", left: "calc(50% + 30px)" }}
          >
            <div className="mb-3 flex items-baseline justify-end gap-3">
              <span className="font-serif-display text-4xl font-normal italic leading-none text-haven-ink md:text-5xl">
                02
              </span>
              <span className="text-[10px] font-light uppercase tracking-[0.32em] text-neutral-500">
                Promise
              </span>
            </div>
            <h4 className="font-serif-display text-xl font-normal italic leading-tight text-haven-ink md:text-[22px]">
              {promises[1].title}
            </h4>
            <p className="mt-2 text-[12px] font-light leading-relaxed text-neutral-700 md:text-[13px]">
              {promises[1].desc}
            </p>
          </div>

          {/* Promise 01 — bottom-left corner, shifted inward to sit in the corner white area
               (+10px right, +5px down for a small nudge) */}
          <div className="pointer-events-auto absolute bottom-14 left-[8rem] z-50 max-w-[300px] translate-x-[10px] translate-y-[5px] text-right md:left-40 md:max-w-[320px]">
            <div className="mb-3 flex items-baseline justify-end gap-3">
              <span className="font-serif-display text-4xl font-normal italic leading-none text-haven-ink md:text-5xl">
                01
              </span>
              <span className="text-[10px] font-light uppercase tracking-[0.32em] text-neutral-500">
                Promise
              </span>
            </div>
            <h4 className="font-serif-display text-xl font-normal italic leading-tight text-haven-ink md:text-[22px]">
              {promises[0].title}
            </h4>
            <p className="mt-2 text-[12px] font-light leading-relaxed text-neutral-700 md:text-[13px]">
              {promises[0].desc}
            </p>
          </div>

          {/* Promise 03 — top-right, sitting under the nav */}
          <div className="pointer-events-auto absolute right-10 top-4 z-50 max-w-[280px] text-right md:right-16 md:top-8 md:max-w-[300px]">
            <div className="mb-3 flex items-baseline justify-end gap-3">
              <span className="text-[10px] font-light uppercase tracking-[0.32em] text-neutral-500">
                Promise
              </span>
              <span className="font-serif-display text-4xl font-normal italic leading-none text-haven-ink md:text-5xl">
                03
              </span>
            </div>
            <h4 className="font-serif-display text-xl font-normal italic leading-tight text-haven-ink md:text-[22px]">
              {promises[2].title}
            </h4>
            <p className="mt-2 text-[12px] font-light leading-relaxed text-neutral-700 md:text-[13px]">
              {promises[2].desc}
            </p>
          </div>
        </div>

        {/* Scroll hint */}
        <div
          ref={scrollHintRef}
          className="absolute bottom-6 left-1/2 z-50 flex -translate-x-1/2 flex-col items-center text-[10px] uppercase tracking-[0.3em] text-neutral-500"
        >
          <span>Scroll to unfold</span>
          <i className="ri-arrow-down-line mt-1 text-base" />
        </div>
      </div>
    </section>
  );
}